import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastService } from 'src/app/shared/services/toast.service';
import * as constants from 'src/app/config/app-constants';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { FleetManagementService } from '../../services/fleet-management.service';
import { DeviceSummary } from '../../models/deviceSummary.model';
import { MatAccordion } from '@angular/material/expansion';
import { AuthorizationService } from 'src/app/internal-user/customer-management/services/authorization.service';
import { AuthService } from 'src/app/auth/auth.service';

@Component({
    selector: 'app-edge-appliance-details',
    templateUrl: './edge-appliance-details.component.html',
    styleUrls: ['./edge-appliance-details.component.scss'],
    standalone: false
})
export class EdgeApplianceDetailsComponent implements OnInit {

  deviceSummary: DeviceSummary;
  edgeDeviceId: string;
  canEditOsDistro = false;
  osDistroOptions: string[] = [];
  isEditingOsDistro = false;

  @ViewChild(MatAccordion) accordion: MatAccordion;
  accordionCollapsed: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<EdgeApplianceDetailsComponent>
    , @Inject(MAT_DIALOG_DATA) public parentCompData: any
    , private toastService: ToastService
    , private ngxLoader: NgxUiLoaderService
    , public fleetManagementService: FleetManagementService
    , public authorizationService: AuthorizationService
    , private readonly authService: AuthService) { }

  ngOnInit() {
    this.authService.profile$.subscribe({
      next: (profile) => {
        this.canEditOsDistro = profile.hasRole('Parata Dev');
      },
      error: () => {
        this.canEditOsDistro = false;
      }
    });

    this.fleetManagementService.getOsDistros().subscribe({
      next: (options) => { this.osDistroOptions = options; },
      error: () => { this.osDistroOptions = []; }
    });

    this.edgeDeviceId = this.parentCompData.edgeDeviceId;

    const loadingKey = 'LoadDeviceSummary';
    this.ngxLoader.start(loadingKey);

    this.fleetManagementService.getEdgeDeviceSummary(this.edgeDeviceId).subscribe({
      next: deviceSummary => {
        this.ngxLoader.stop(loadingKey);

        if (!deviceSummary) {
          console.error(`No details could be located for device ID ${this.edgeDeviceId}.`);
          this.toastService.openToast(`Details for device ${this.edgeDeviceId} could not be found.`, constants.ToastPanelType.error);
          this.dialogRef.close(false);
        }
        this.deviceSummary = deviceSummary;

        this.getUserDetails();

        this.getHostInformation(); //refresh the IP. It may have changed from cached
      },
      error: err => {
        console.error(err);
        this.ngxLoader.stop(loadingKey);
        this.toastService.openToast(`Unexpected error fetching details for device ${this.edgeDeviceId}.`, constants.ToastPanelType.error);
        this.dialogRef.close();
      }
    });
  }

  getUserDetails() {
    this.authorizationService.getUser(this.deviceSummary.user.email).subscribe({
      next: user => {
        this.deviceSummary.lastDeployed = this.getLastDeployed(this.deviceSummary.timestamp, user.firstName + ' ' + user.lastName);
      },
      error: err => {
        this.deviceSummary.lastDeployed = this.getLastDeployed(this.deviceSummary.timestamp, this.deviceSummary.user.email);
      }
    });
  }



  private getHostInformation(): void {
    this.fleetManagementService.getDeviceInformation(this.edgeDeviceId).subscribe({
      next: (v) => {
        try {
          const result = JSON.parse(v);
          const deviceDetails = JSON.parse(result.data);
          this.deviceSummary.ipAddress = this.separateIpAddresses(deviceDetails.IPAddress);
        }
        catch (err) {
          console.log(err);
          this.deviceSummary.ipAddress = "Unknown";
        }
      },
      error: (e) => {
        console.log(e);
        this.deviceSummary.ipAddress = "Unknown";
      }
    });
  }

  private separateIpAddresses(ipAddress): string {
    const ipv4Regex = /\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/;
    const ipv4Match = ipAddress.match(ipv4Regex);
    return ipv4Match ? ipv4Match[0] : "Unknown";
  }

  onOsDistroChange(value: string) {
    this.fleetManagementService.updateOsDistro(this.edgeDeviceId, value).subscribe({
      next: () => {
        this.deviceSummary.osDistro = value;
        this.isEditingOsDistro = false;
        this.toastService.openToast('OS Distro updated successfully.', constants.ToastPanelType.done);
      },
      error: (err) => {
        console.error('Failed to update OS distro', err);
        this.toastService.openToast('Failed to update OS Distro.', constants.ToastPanelType.error);
      }
    });
  }

  cancelOsDistroEdit() {
    this.isEditingOsDistro = false;
  }

  getFeatureList(systemType: string, features: Array<any>): string {
    return features?.map(feature => {
      let system = this.deviceSummary?.systems?.find(data => data.name == systemType);
      let systemFeature = system?.features?.find(optionalFeature => optionalFeature.name === feature.name);
      return systemFeature.name;
    }).sort((a, b) => a.localeCompare(b)).join(', ') || 'N/A';
  }

  getSystemTypeCount(systemName: string): number {
    return this.deviceSummary?.systems?.filter(system =>
      this.getCanonicalSystemType(system.name) === systemName
    ).length;
  }

  getSystemTypeDisplayName(systemType: string): string {
    return this.getCanonicalSystemType(systemType);
  }

  getCanonicalSystemType(systemType: string): string {
    const normalizedSystemType = systemType?.replace(/[\s()_-]/g, '').toLowerCase();
    if (normalizedSystemType === 'atpcaps') {
      return 'Tempo-ATP';
    }

    return normalizedSystemType === 'caps' ? 'Tempo' : systemType;
  }

  get distinctSystemTypes() {
    let names = this.deviceSummary?.systems?.map(control => this.getCanonicalSystemType(control.name));
    return [...new Set(names)];
  }

  toggleAccordion(): void {
    if (this.accordionCollapsed) {
      this.accordionCollapsed = false;
      this.accordion.openAll();
    }
    else {
      this.accordionCollapsed = true;
      this.accordion.closeAll();
    }
  }

  getDaySuffix(day: number): string {
    if (day >= 11 && day <= 13) {
      return 'th';
    }

    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  getLastDeployed(timestamp: any, userName: string): string {

    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'N/A'; // Handle invalid dates

    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const dayOfWeek = days[date.getDay()];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const daySuffix = this.getDaySuffix(day);
    const year = date.getFullYear();

    let hours = date.getHours();
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12; // Convert to 12-hour format

    return `${dayOfWeek}, ${month} ${day}${daySuffix} ${year}, ${hours}${ampm} by ${userName}`;
  }

}
